/**
 * RFP intake: structured controls map to form_data_json.rfp[sectionId][key].
 * input: text | textarea | radio | multicheck | multiselect | tags | readonly | channelBudgets | date | select
 */
import {
  sortOptionsByLabel,
  KPI_PRIMARY_OPTIONS,
  KPI_SECONDARY_OPTIONS,
  AGE_BLOCK_OPTIONS,
  GENDER_AUDIENCE_OPTIONS,
  HHI_OPTIONS,
  PSYCHOGRAPHIC_OPTIONS,
  DMA_OPTIONS as DMA_OPTIONS_RAW,
  CBSA_OPTIONS as CBSA_OPTIONS_RAW,
  VENUE_TYPE_GEO_OPTIONS,
  CONTEXTUAL_PREF_OPTIONS,
  FIRST_PARTY_DATA_OPTIONS,
  RETARGETING_OPTIONS,
  PLACEMENT_CHANNEL_OPTIONS,
  PRICING_MODEL_OPTIONS,
  COMMISSION_STRUCTURE_OPTIONS,
  DAYPART_OPTIONS,
  BLACKOUT_PRESET_OPTIONS,
  FLIGHT_PATTERN_OPTIONS,
  AD_FORMAT_OPTIONS,
  SCREEN_VENUE_TYPE_OPTIONS,
  PLACEMENT_RESTRICTION_OPTIONS,
  SHARE_OF_VOICE_OPTIONS,
  ROADBLOCK_OPTIONS,
  FREQUENCY_CAP_OPTIONS,
  IAB_DISPLAY_SIZE_OPTIONS,
  VAST_VIDEO_OPTIONS,
  ATTRIBUTION_OPTIONS,
  DATA_PRIVACY_OPTIONS,
  PROPOSAL_FORMAT_OPTIONS,
  COUNTRY_OPTIONS,
  US_STATE_OPTIONS,
  INTERCEPT_SPOT_LENGTH_OPTIONS,
} from './rfpOptionLists';

const DMA_OPTIONS = sortOptionsByLabel(DMA_OPTIONS_RAW);
const CBSA_OPTIONS = sortOptionsByLabel(CBSA_OPTIONS_RAW);

const CAMPAIGN_GOAL_OPTIONS = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'dr', label: 'Direct response' },
  { value: 'foot_traffic', label: 'Foot traffic / visits' },
  { value: 'loyalty', label: 'Loyalty / retention' },
  { value: 'launch', label: 'Product launch' },
  { value: 'competitive_conquest', label: 'Competitive conquest' },
  { value: 'other', label: 'Other (notes)' },
];

export const RFP_SECTIONS = [
  {
    id: 'identity',
    title: 'Advertiser & agency identity',
    fields: [
      { key: 'advertiserName', label: 'Advertiser name', input: 'text', required: true },
      { key: 'brand', label: 'Brand', input: 'text' },
      { key: 'parentCompany', label: 'Parent company', input: 'text' },
      { key: 'agencyName', label: 'Agency name (if applicable)', input: 'text' },
      { key: 'agencyContact', label: 'Agency contact', input: 'textarea' },
      { key: 'campaignContact', label: 'Campaign / primary point of contact', input: 'textarea' },
      { key: 'billingContact', label: 'Billing contact', input: 'textarea' },
      { key: 'billingStreet', label: 'Billing street address', input: 'text' },
      { key: 'billingStreet2', label: 'Street line 2 (suite, floor)', input: 'text' },
      { key: 'billingCity', label: 'City', input: 'text' },
      { key: 'billingState', label: 'State / province', input: 'select', options: US_STATE_OPTIONS },
      { key: 'billingZip', label: 'ZIP / postal code', input: 'text' },
      { key: 'billingCountry', label: 'Country', input: 'select', options: COUNTRY_OPTIONS },
      {
        key: 'agencyCommissionRate',
        label: 'Agency commission rate (if applicable)',
        input: 'radio',
        options: [
          { value: 'none', label: 'None' },
          { value: '10', label: '10%' },
          { value: '15', label: '15%' },
          { value: 'other', label: 'Other (notes in identity)' },
        ],
      },
      { key: 'identityNotes', label: 'Additional identity / billing notes', input: 'textarea' },
    ],
  },
  {
    id: 'objectives',
    title: 'Campaign objectives & strategy',
    fields: [
      {
        key: 'campaignGoals',
        label: 'Campaign goals (select all that apply)',
        input: 'multicheck',
        options: CAMPAIGN_GOAL_OPTIONS,
      },
      { key: 'kpiPrimary', label: 'Primary KPI', input: 'radio', options: KPI_PRIMARY_OPTIONS },
      {
        key: 'kpisSecondary',
        label: 'Additional KPIs (multi-select)',
        input: 'multicheck',
        options: KPI_SECONDARY_OPTIONS,
      },
      { key: 'primaryMessage', label: 'Primary message / value proposition', input: 'textarea' },
      { key: 'callToAction', label: 'Call to action', input: 'textarea' },
      { key: 'competitiveRestrictions', label: 'Competitive restrictions', input: 'textarea' },
    ],
  },
  {
    id: 'audience',
    title: 'Audience targeting',
    fields: [
      { key: 'ageBlock', label: 'Target age', input: 'radio', options: AGE_BLOCK_OPTIONS },
      { key: 'genderAudience', label: 'Gender', input: 'radio', options: GENDER_AUDIENCE_OPTIONS },
      {
        key: 'hhiRanges',
        label: 'Household income (select all that apply)',
        input: 'multicheck',
        options: HHI_OPTIONS,
      },
      {
        key: 'psychographic',
        label: 'Psychographic / behavioral (multi-select)',
        input: 'multicheck',
        options: PSYCHOGRAPHIC_OPTIONS,
      },
      {
        key: 'dmaMarkets',
        label: 'DMA targeting (multi-select, A–Z)',
        input: 'multiselect',
        options: DMA_OPTIONS,
        selectSize: 8,
        help: 'Select at least one DMA or one CBSA below (required to save or send).',
      },
      {
        key: 'cbsaMarkets',
        label: 'CBSA targeting (multi-select, A–Z)',
        input: 'multiselect',
        options: CBSA_OPTIONS,
        selectSize: 6,
        help: 'Select at least one CBSA or one DMA above (required to save or send).',
      },
      {
        key: 'zipCodes',
        label: 'ZIP / postal codes',
        input: 'tags',
        placeholder: 'e.g. 60614',
        help: 'Add one at a time, paste a comma/newline-separated list, or use “Add all from paste”.',
      },
      {
        key: 'venueTypesGeo',
        label: 'Venue / environment types (geo)',
        input: 'multicheck',
        options: VENUE_TYPE_GEO_OPTIONS,
      },
      {
        key: 'contextualPreferences',
        label: 'Contextual preferences',
        input: 'multicheck',
        options: CONTEXTUAL_PREF_OPTIONS,
      },
      {
        key: 'firstPartyDataMatch',
        label: 'First-party data match',
        input: 'multicheck',
        options: FIRST_PARTY_DATA_OPTIONS,
      },
      {
        key: 'retargetingLookalike',
        label: 'Retargeting / lookalike',
        input: 'multicheck',
        options: RETARGETING_OPTIONS,
      },
      { key: 'audienceNotes', label: 'Additional audience / geo notes', input: 'textarea' },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory & placement specifications',
    fields: [
      {
        key: 'placementChannels',
        label: 'Channels / placement types (select all that apply)',
        input: 'multicheck',
        options: PLACEMENT_CHANNEL_OPTIONS,
      },
      {
        key: 'adFormats',
        label: 'Requested ad formats',
        input: 'multicheck',
        options: AD_FORMAT_OPTIONS,
      },
      {
        key: 'screenVenueTypes',
        label: 'Screen / venue types',
        input: 'multicheck',
        options: SCREEN_VENUE_TYPE_OPTIONS,
      },
      {
        key: 'placementRestrictions',
        label: 'Placement restrictions',
        input: 'multicheck',
        options: PLACEMENT_RESTRICTION_OPTIONS,
      },
      { key: 'shareOfVoice', label: 'Share of voice', input: 'radio', options: SHARE_OF_VOICE_OPTIONS },
      { key: 'roadblockExclusivity', label: 'Roadblock / exclusivity', input: 'radio', options: ROADBLOCK_OPTIONS },
      {
        key: 'estimatedImpressionsOrSpots',
        label: 'Estimated impression volume or spot count',
        input: 'radio',
        options: [
          { value: 'lt500k', label: 'Under 500K impressions' },
          { value: '500k_2m', label: '500K – 2M' },
          { value: '2m_10m', label: '2M – 10M' },
          { value: '10m_plus', label: '10M+' },
          { value: 'spots_tbd', label: 'Spot-based / TBD' },
          { value: 'custom', label: 'Custom (notes)' },
        ],
      },
      {
        key: 'frequencyCap',
        label: 'Frequency cap',
        input: 'radio',
        options: FREQUENCY_CAP_OPTIONS,
      },
      { key: 'inventoryNotes', label: 'Additional inventory notes', input: 'textarea' },
    ],
  },
  {
    id: 'budgetPricing',
    title: 'Budget & pricing',
    fields: [
      {
        key: 'budgetAllocations',
        label: 'Budget by selected channel ($)',
        input: 'channelBudgets',
        help: 'Allocations appear when more than one channel is selected above. Enter a dollar amount for each.',
      },
      {
        key: 'pricingModelPreference',
        label: 'Pricing model',
        input: 'radio',
        options: PRICING_MODEL_OPTIONS,
      },
      {
        key: 'agencyCommissionStructure',
        label: 'Commission structure',
        input: 'radio',
        options: COMMISSION_STRUCTURE_OPTIONS,
      },
      {
        key: 'paymentTerms',
        label: 'Payment terms',
        input: 'readonly',
        readonlyText: 'All direct sales: NET 30.',
        storedValue: 'NET 30',
      },
      { key: 'pricingNotes', label: 'Additional pricing / packaging notes', input: 'textarea' },
    ],
  },
  {
    id: 'flighting',
    title: 'Flight dates & scheduling',
    fields: [
      {
        key: 'flightScheduleStart',
        label: 'Flight start date',
        input: 'date',
        help: 'Should align with “Flight start” in Deal & recipient above; adjust here if this RFP uses a different window.',
      },
      {
        key: 'flightScheduleEnd',
        label: 'Flight end date',
        input: 'date',
      },
      {
        key: 'dayparting',
        label: 'Dayparting / schedule windows',
        input: 'multicheck',
        options: DAYPART_OPTIONS,
      },
      {
        key: 'blackoutDates',
        label: 'Blackouts',
        input: 'multicheck',
        options: BLACKOUT_PRESET_OPTIONS,
      },
      {
        key: 'flightingPattern',
        label: 'Flighting pattern',
        input: 'radio',
        options: FLIGHT_PATTERN_OPTIONS,
      },
      {
        key: 'flightTimeNotes',
        label: 'Specific hours or date exceptions (short)',
        input: 'textarea',
      },
    ],
  },
  {
    id: 'creative',
    title: 'Creative & ad materials',
    fields: [
      {
        key: 'adUnitSizes',
        label: 'Display / static — IAB ad unit sizes (multi-select)',
        input: 'multicheck',
        options: IAB_DISPLAY_SIZE_OPTIONS,
        help: 'Static and standard display creatives must use IAB standard sizes. Video uses VAST selections below.',
      },
      {
        key: 'videoSpecs',
        label: 'Video — VAST / digital video (multi-select)',
        input: 'multicheck',
        options: VAST_VIDEO_OPTIONS,
      },
      { key: 'richMedia', label: 'Rich media or interactive requirements', input: 'textarea' },
      { key: 'creativeVersions', label: 'Number of creative versions / rotations', input: 'text' },
      { key: 'creativeDeadline', label: 'Creative submission deadline', input: 'date' },
      { key: 'mediaCompanyProducesCreative', label: 'Media company to produce creative?', input: 'textarea' },
      {
        key: 'thirdPartyAdTags',
        label: '3rd-party verification / tags (if buyer-supplied)',
        input: 'textarea',
        help: 'Context Networks is the ad server for CPMN; list any buyer verification tags here.',
      },
    ],
  },
  {
    id: 'measurement',
    title: 'Measurement & reporting',
    fields: [
      {
        key: 'adServingNote',
        label: 'Ad serving',
        input: 'readonly',
        readonlyText: 'Campaigns are served on Context Networks (CPMN). No third-party ad server selection is required.',
        storedValue: 'context_networks',
      },
      { key: 'trackingPixels', label: 'Tracking pixels or tags required', input: 'textarea' },
      {
        key: 'attributionMethodology',
        label: 'Attribution methodology (select all that apply)',
        input: 'multicheck',
        options: ATTRIBUTION_OPTIONS,
      },
      {
        key: 'reportingFrequency',
        label: 'Reporting frequency',
        input: 'radio',
        options: [
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'end_of_flight', label: 'End of flight only' },
        ],
      },
      { key: 'reportBreakouts', label: 'Required report breakouts', input: 'textarea' },
      { key: 'brandLiftStudies', label: 'Brand lift or audience study requirements', input: 'textarea' },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance & legal',
    fields: [
      { key: 'editorialAdjacency', label: 'Editorial adjacency restrictions', input: 'textarea' },
      { key: 'industryCompliance', label: 'Industry-specific compliance', input: 'textarea' },
      {
        key: 'dataPrivacy',
        label: 'Data privacy & regulatory (multi-select)',
        input: 'multicheck',
        options: DATA_PRIVACY_OPTIONS,
      },
      { key: 'exclusivityLegal', label: 'Exclusivity requirements', input: 'textarea' },
      { key: 'cancellationTerms', label: 'Cancellation terms preference', input: 'textarea' },
    ],
  },
  {
    id: 'proposalResponse',
    title: 'Proposal response requirements',
    fields: [
      { key: 'proposalDueDate', label: 'Proposal due date', input: 'date' },
      {
        key: 'proposalFormat',
        label: 'Preferred proposal format (multi-select)',
        input: 'multicheck',
        options: PROPOSAL_FORMAT_OPTIONS,
      },
      {
        key: 'competitiveBids',
        label: 'Competitive bids solicited?',
        input: 'radio',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      { key: 'decisionTimelineDate', label: 'Target decision date', input: 'date' },
      { key: 'requiredProposalElements', label: 'Required elements', input: 'textarea' },
    ],
  },
  {
    id: 'cpmn',
    title: 'CPMN®-specific',
    fields: [
      {
        key: 'venueCategoryPreference',
        label: 'Venue category preference',
        input: 'multicheck',
        options: [
          { value: 'gaming', label: 'Gaming' },
          { value: 'casino_floor', label: 'Casino floor' },
          { value: 'atm_kiosk', label: 'ATM / kiosk' },
          { value: 'sports_bar', label: 'Sports bar' },
          { value: 'ctv', label: 'CTV' },
          { value: 'mobile_app', label: 'Mobile app' },
          { value: 'mixed', label: 'Mixed' },
        ],
      },
      { key: 'stateDmaTargeting', label: 'State / DMA notes (optional)', input: 'textarea' },
      { key: 'loyaltyDataMatchInterest', label: 'Loyalty data match interest', input: 'textarea' },
      {
        key: 'deviceType',
        label: 'Device / surface type',
        input: 'multicheck',
        options: [
          { value: 'gaming_screen', label: 'Gaming screen' },
          { value: 'interceptad', label: 'InterceptAD™' },
          { value: 'atm_kiosk', label: 'ATM / kiosk display' },
        ],
      },
      {
        key: 'interceptSpotLengths',
        label: 'InterceptAD™ / linear spot lengths (multi-select)',
        input: 'multicheck',
        options: INTERCEPT_SPOT_LENGTH_OPTIONS,
      },
      {
        key: 'dspOfRecord',
        label: 'DSP of record (if programmatic)',
        input: 'textarea',
        help:
          'If inventory is bought or trafficked through a demand-side platform (e.g. Place Exchange, Vistar, Hivestack), name it here so ops can route tags and seats. Leave blank for direct IO only.',
      },
      { key: 'whiteLabelPmp', label: 'White-label / PMP', input: 'textarea' },
    ],
  },
];

function emptyValueForField(field) {
  const input = field.input || 'text';
  if (input === 'multicheck' || input === 'multiselect' || input === 'tags') return [];
  if (input === 'channelBudgets') return {};
  if (input === 'readonly') return field.storedValue != null ? String(field.storedValue) : '';
  if (input === 'date' || input === 'select') return '';
  return '';
}

export function buildEmptyRfpState() {
  const rfp = {};
  for (const sec of RFP_SECTIONS) {
    rfp[sec.id] = {};
    for (const f of sec.fields) {
      rfp[sec.id][f.key] = emptyValueForField(f);
    }
  }
  return rfp;
}

/** Merge saved API blob into template; coerce types for structured fields */
export function mergeRfpFromSaved(blobRfp, template) {
  const merged = JSON.parse(JSON.stringify(template));
  if (!blobRfp || typeof blobRfp !== 'object') return merged;
  for (const sec of RFP_SECTIONS) {
    const incoming = blobRfp[sec.id];
    if (!incoming || typeof incoming !== 'object') continue;
    for (const f of sec.fields) {
      const raw = incoming[f.key];
      const input = f.input || 'text';
      if (input === 'multicheck' || input === 'multiselect' || input === 'tags') {
        if (Array.isArray(raw)) merged[sec.id][f.key] = raw.map(String);
        else if (typeof raw === 'string' && raw.trim()) {
          merged[sec.id][f.key] = raw
            .split(/[,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        } else merged[sec.id][f.key] = [];
      } else if (input === 'channelBudgets') {
        merged[sec.id][f.key] = raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {};
      } else if (raw !== undefined && raw !== null) {
        merged[sec.id][f.key] = raw;
      }
    }
  }

  const legacyAudience = blobRfp.audience;
  if (legacyAudience && typeof legacyAudience === 'object') {
    const parts = [];
    for (const k of ['demographics', 'psychographic', 'geographicTargeting']) {
      const t = legacyAudience[k];
      if (typeof t === 'string' && t.trim()) parts.push(`${k}: ${t.trim()}`);
    }
    if (parts.length) {
      const prev = (merged.audience.audienceNotes || '').trim();
      merged.audience.audienceNotes = [prev, ...parts].filter(Boolean).join('\n\n');
    }
  }

  merged.budgetPricing.paymentTerms = 'NET 30';

  const legId = blobRfp.identity;
  if (legId && typeof legId === 'object' && typeof legId.billingAddress === 'string' && legId.billingAddress.trim()) {
    const n = (merged.identity.identityNotes || '').trim();
    merged.identity.identityNotes = [n, `Legacy billing address: ${legId.billingAddress.trim()}`].filter(Boolean).join('\n\n');
  }

  const legProposal = blobRfp.proposalResponse;
  if (legProposal && typeof legProposal === 'object') {
    if (typeof legProposal.decisionTimeline === 'string' && legProposal.decisionTimeline.trim()) {
      const req = (merged.proposalResponse.requiredProposalElements || '').trim();
      merged.proposalResponse.requiredProposalElements = [req, `Decision timeline (legacy): ${legProposal.decisionTimeline.trim()}`]
        .filter(Boolean)
        .join('\n\n');
    }
    if (typeof legProposal.competitiveBids === 'string' && legProposal.competitiveBids.trim() && !merged.proposalResponse.competitiveBids) {
      const t = legProposal.competitiveBids.trim().toLowerCase();
      merged.proposalResponse.competitiveBids = /\byes\b|\btrue\b|competitive/i.test(t) && !/\bno\b|\bfalse\b/i.test(t) ? 'yes' : 'no';
    }
  }

  if (Array.isArray(merged.cpmn?.deviceType)) {
    merged.cpmn.deviceType = merged.cpmn.deviceType.map((d) => (d === 'tv_intercept' ? 'interceptad' : d));
  }
  const legCpmn = blobRfp.cpmn;
  if (legCpmn && typeof legCpmn === 'object' && typeof legCpmn.tvInterceptSpots === 'string' && legCpmn.tvInterceptSpots.trim()) {
    const n = (merged.cpmn.whiteLabelPmp || '').trim();
    merged.cpmn.whiteLabelPmp = [n, `Legacy spot note: ${legCpmn.tvInterceptSpots.trim()}`].filter(Boolean).join('\n\n');
  }

  const legObj = blobRfp.objectives;
  if (legObj && typeof legObj === 'object') {
    const bits = [];
    if (typeof legObj.campaignGoal === 'string' && legObj.campaignGoal.trim()) bits.push(`Campaign goal (legacy): ${legObj.campaignGoal.trim()}`);
    if (typeof legObj.kpis === 'string' && legObj.kpis.trim()) bits.push(`KPIs (legacy): ${legObj.kpis.trim()}`);
    if (bits.length) {
      const pm = (merged.objectives.primaryMessage || '').trim();
      merged.objectives.primaryMessage = [pm, ...bits].filter(Boolean).join('\n\n');
    }
  }

  const legCreative = blobRfp.creative;
  if (legCreative && typeof legCreative === 'object' && typeof legCreative.staticDisplaySpecs === 'string' && legCreative.staticDisplaySpecs.trim()) {
    const rm = (merged.creative.richMedia || '').trim();
    merged.creative.richMedia = [rm, `Legacy static/display note: ${legCreative.staticDisplaySpecs.trim()}`].filter(Boolean).join('\n\n');
  }

  const legMeas = blobRfp.measurement;
  if (legMeas && typeof legMeas === 'object' && typeof legMeas.preferredAdServer === 'string' && legMeas.preferredAdServer.trim()) {
    const px = (merged.measurement.trackingPixels || '').trim();
    merged.measurement.trackingPixels = [px, `Legacy ad server note: ${legMeas.preferredAdServer.trim()}`].filter(Boolean).join('\n\n');
  }
  if (legMeas && typeof legMeas === 'object' && typeof legMeas.attributionMethodology === 'string' && legMeas.attributionMethodology.trim()) {
    if (!Array.isArray(merged.measurement.attributionMethodology) || merged.measurement.attributionMethodology.length === 0) {
      merged.measurement.trackingPixels = [
        (merged.measurement.trackingPixels || '').trim(),
        `Legacy attribution: ${legMeas.attributionMethodology.trim()}`,
      ]
        .filter(Boolean)
        .join('\n\n');
    }
  }

  return merged;
}

/** RFP sections shown on affiliate campaign intake (no billing / proposal / identity billing). */
export const AFFILIATE_RFP_SECTION_IDS = [
  'objectives',
  'audience',
  'inventory',
  'flighting',
  'creative',
  'measurement',
  'compliance',
];

export const AFFILIATE_RFP_SECTIONS = RFP_SECTIONS.filter((s) =>
  AFFILIATE_RFP_SECTION_IDS.includes(s.id)
);
